/**
 * The round's wheel — the app's port of `design/gt-logic.js#rWheel`
 * (DESIGN-CHANGELOG 2026-08-26 "Wheel: dim hat, hover to look", "Player hue
 * carries all the way onto the wheel", "The win flash wears the hue"). Shared
 * by both trees; the desktop passes `onHov`, the phone does not.
 *
 * Each nomination is a slice, its width its odds. The wedge face IS the cover:
 * the capsule's top-centre sits at the hub and the art hangs outward along
 * the wedge's bisector, under a heavy `--gt-ink` scrim — nothing is decided
 * yet, so the whole board sits dim. The scrim clears on the slice under the
 * cursor (desktop) or under the pointer mid-spin, and lifts on the winner.
 * No letters on the slices — the pin card and the hover panel name them.
 *
 * ROTATION IS A NUMBER, NOT A TRANSITION. The prototype animated with a CSS
 * transition on the leader's device; here `rot` arrives from `spinPose()`
 * against the shared clock, re-rendered per frame by `useRoundClock`, so
 * every screen is at the same pose at the same instant and a client that
 * joins mid-spin starts at the right angle rather than from rest.
 */

import { useEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';

import { spinPose } from '../lib/round.ts';
import type { RoundSpin } from '../lib/round.ts';
import { Avatar } from './Avatar.tsx';
import { useArt } from './useArt.ts';
import type { RoundNomByVM, RoundWedgeVM } from '../store/selectors.round.ts';

const MONO = 'var(--gt-font-mono)';

function arc(r: number, a0: number, a1: number): [number, number][] {
  const n = Math.max(1, Math.ceil(Math.abs(a1 - a0) / 6));
  const out: [number, number][] = [];
  for (let k = 0; k <= n; k++) {
    const deg = a0 + ((a1 - a0) * k) / n;
    const rad = ((deg - 90) * Math.PI) / 180;
    out.push([100 + r * Math.cos(rad), 100 + r * Math.sin(rad)]);
  }
  return out;
}
const pathOf = (pts: [number, number][]): string =>
  'M' + pts.map((p) => p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join(' L') + ' Z';
const wedgePath = (a0: number, a1: number): string =>
  'M100 100 L' + arc(97, a0, a1).map((p) => p[0].toFixed(2) + ' ' + p[1].toFixed(2)).join(' L') + ' Z';

export interface WheelProps {
  wedges: readonly RoundWedgeVM[];
  /** degrees clockwise, from `spinPose` — the resting pose */
  rot: number;
  /**
   * The live spin record and the shared clock. While a spin is in flight the
   * wheel drives its own transform from these in a rAF loop, WITHOUT a React
   * render per frame: re-rendering the page (72 tiles, the SVG's
   * foreignObjects) sixty times a second is what made the spin feel framey
   * (Marcus, staging 2026-08-26). React only re-renders when the slice under
   * the pointer changes — see `useRoundClock`.
   */
  spin?: RoundSpin | null;
  serverNow?: () => number;
  /** rendered px, square */
  px: number;
  /** the landed winner's appid, 0 for none */
  winId: number;
  /** the winner's nominator hue — the flash outline */
  winHue: string;
  /** distinct per mount: clipPath ids are document-global */
  keyPrefix: string;
  /** desktop: the slice under the cursor, and where the cursor is */
  onHov?: (id: number | null, x?: number, y?: number) => void;
  /** a slice is a game: click opens it (anyone in the round) */
  onOpen?: (id: number) => void;
  style?: CSSProperties;
}

/** One wedge's art — its own component so `useArt` is one hook per slice. */
function WedgeArt({
  w,
  clipId,
}: {
  w: RoundWedgeVM;
  clipId: string;
}): JSX.Element {
  const art = useArt(w.nom.id + 'p', w.nom.coverCands);
  // The capsule fills the wedge: anchored top-centre at the hub and rotated
  // onto the bisector. Half-angle capped so a 2- or 3-slice wheel does not
  // scale one capsule up to a single letter; a whole-circle wedge fills the disc.
  const full = w.span >= 340;
  // A wedge wider than 120° wraps past the hub's horizontal, where a box that
  // hangs BELOW the hub cannot reach — the uncovered corner showed as a dark
  // sliver on a weighted two-game wheel (Marcus, staging 2026-08-27). Such a
  // wedge gets a full-disc square rotated onto its bisector instead; the clip
  // trims it to the slice.
  const wide = !full && w.span > 120;
  const half = (Math.min(60, w.span / 2) * Math.PI) / 180;
  const bh = 98;
  const bw = full ? 200 : Math.min(200, 2 * bh * Math.tan(half) + 1);
  const url = art.hasCover ? /url\("([^"]+)"\)/.exec(art.cover)?.[1] : undefined;
  const fill: CSSProperties = url
    ? { backgroundImage: 'url("' + url + '")', backgroundSize: 'cover', backgroundPosition: full || wide ? 'center' : 'center top', backgroundRepeat: 'no-repeat' }
    : { background: w.nom.fall };
  const box: CSSProperties = full
    ? { position: 'absolute', left: 0, top: 0, width: 200, height: 200 }
    : wide
      ? { position: 'absolute', left: 0, top: 0, width: 200, height: 200, transformOrigin: '50% 50%', transform: 'rotate(' + (w.mid + 180).toFixed(2) + 'deg)' }
      : {
        position: 'absolute',
        left: (100 - bw / 2).toFixed(2) + 'px',
        top: '100px',
        width: bw.toFixed(2) + 'px',
        height: bh + 'px',
        transformOrigin: '50% 0',
        transform: 'rotate(' + (w.mid + 180).toFixed(2) + 'deg)',
      };
  return (
    <foreignObject x={0} y={0} width={200} height={200} clipPath={'url(#' + clipId + ')'}>
      <div style={{ position: 'relative', width: 200, height: 200 }}>
        <div style={{ ...fill, ...box }} />
      </div>
    </foreignObject>
  );
}

export function Wheel({ wedges, rot, px, winId, winHue, keyPrefix, onHov, onOpen, style, spin, serverNow }: WheelProps): JSX.Element {
  const bid = 'rbase-' + keyPrefix;
  const winW = wedges.find((w) => w.id === winId);
  const ref = useRef<SVGSVGElement | null>(null);
  const seq = spin ? spin.seq : 0;
  const startedAt = spin ? spin.startedAt : 0;

  // The imperative driver: one transform write per frame, no React in the
  // loop. Ends itself on landing; the React tree then owns `rot` again.
  useEffect(() => {
    const el = ref.current;
    if (!el || !spin || !serverNow) return;
    let raf = 0;
    const step = () => {
      const p = spinPose(spin, serverNow());
      el.style.transform = 'rotate(' + p.rot + 'deg)';
      if (!p.landed) raf = requestAnimationFrame(step);
    };
    step();
    return () => cancelAnimationFrame(raf);
    // keyed on the record's identity, not the object: the store installs a
    // fresh snapshot on every realtime echo, and restarting the loop for the
    // same spin would be harmless but wasteful.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq, startedAt, serverNow]);

  return (
    <svg
      ref={ref}
      viewBox="0 0 200 200"
      width={px}
      height={px}
      style={{ display: 'block', transform: 'rotate(' + rot + 'deg)', willChange: 'transform', ...style }}
      aria-hidden="true"
    >
      {/* Base plate, always drawn: the wheel is furniture, not a result — an
          empty wheel still shows a wheel to nominate into. */}
      <circle cx={100} cy={100} r={97} fill="var(--gt-tile)" />
      <clipPath id={bid}>
        <circle cx={100} cy={100} r={97} />
      </clipPath>
      <foreignObject x={0} y={0} width={200} height={200} clipPath={'url(#' + bid + ')'}>
        <div style={{ width: 200, height: 200, background: 'var(--gt-checker)' }} />
      </foreignObject>
      {wedges.map((w) => {
        const d = wedgePath(w.a0, w.a1);
        const cid = 'rsl-' + keyPrefix + '-' + w.id;
        return (
          <g key={w.id}>
            {/* opaque backing: --gt-raised is translucent and the checker showed through */}
            <path d={d} fill="var(--gt-bg)" />
            <path d={d} fill={w.isWin ? 'var(--accSoft)' : w.i % 2 ? 'var(--gt-tile)' : 'var(--gt-raised)'} />
            <clipPath id={cid}>
              <path d={d} />
            </clipPath>
            <WedgeArt w={w} clipId={cid} />
            {/* the scrim: dim hat, clear under the pointer, lifted on the winner */}
            <path d={d} fill="var(--gt-ink)" opacity={w.scrim} />
            {/* tier rim at the very edge — a marker on the art, not a frame */}
            <path d={pathOf(arc(97, w.a0, w.a1).concat(arc(94, w.a1, w.a0)))} fill={w.nom.tierClr} opacity={0.9} />
            <path d={d} fill="none" stroke="var(--gt-line)" strokeWidth={0.6} />
            {onHov || onOpen ? (
              <path
                d={d}
                fill="transparent"
                style={{ pointerEvents: 'all', cursor: onOpen ? 'pointer' : 'default' }}
                onMouseMove={onHov ? (e) => onHov(w.id, e.clientX, e.clientY) : undefined}
                onMouseLeave={onHov ? () => onHov(null) : undefined}
                onClick={
                  onOpen
                    ? (e) => {
                        e.stopPropagation();
                        onOpen(w.id);
                      }
                    : undefined
                }
              />
            ) : null}
          </g>
        );
      })}
      <path d={pathOf(arc(97, 0, 360))} fill="none" stroke="var(--gt-border-3)" strokeWidth={1} />
      {/* the win flash, drawn LAST so no rim or ring paints over it */}
      {winW ? (
        <path
          d={wedgePath(winW.a0, winW.a1)}
          fill="none"
          stroke={winHue}
          strokeWidth={2.2}
          style={{ animation: 'gt-rwin .45s steps(2,end) 6' }}
        />
      ) : null}
    </svg>
  );
}

/** The pixel-triangle pointer at 12 o'clock (both mirrors, verbatim). */
export function WheelPointer({ style }: { style?: CSSProperties }): JSX.Element {
  return (
    <svg
      width={26}
      height={24}
      viewBox="0 0 9 8"
      style={{ display: 'block', transform: 'rotate(180deg)', ...style }}
      aria-hidden="true"
    >
      <rect x="4" y="0" width="1" height="2" fill="var(--acc)" />
      <rect x="3" y="2" width="3" height="2" fill="var(--acc)" />
      <rect x="2" y="4" width="5" height="2" fill="var(--acc)" />
      <rect x="1" y="6" width="7" height="2" fill="var(--acc)" />
    </svg>
  );
}

/**
 * Re-render the page ONLY when something the React tree paints has changed:
 * the slice under the pointer (the pin card, the undimmed wedge) or the
 * landing. The wheel's own rotation never goes through React — `Wheel`
 * drives it imperatively — so this fires a handful of times a second near
 * the end of a spin and not sixty. A 33 ms interval, not rAF, because the
 * frame loop is throttled in a background tab and the landing must still
 * happen (the prototype's own reasoning).
 */
export function useRoundClock(spin: RoundSpin | null | undefined, serverNow: () => number): number {
  const [tick, setTick] = useState(0);
  const seq = spin ? spin.seq : 0;
  const startedAt = spin ? spin.startedAt : 0;
  useEffect(() => {
    if (!spin) return;
    if (spinPose(spin, serverNow()).landed) return;
    let last = -2;
    const iv = setInterval(() => {
      const p = spinPose(spin, serverNow());
      const key = p.landed ? -1 : p.pinIndex;
      if (key !== last) {
        last = key;
        setTick((t) => t + 1);
      }
      if (p.landed) clearInterval(iv);
    }, 33);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [seq, startedAt, serverNow]);
  return tick;
}

/** The three-slot quarter pocket: a coin where a quarter is still held, the dark socket where one was spent. */
export function QuarterSocket({ size }: { size: number }): JSX.Element {
  // `design/gt-logic.js#R_SOCKET` — the coin's silhouette in --gt-line, inset in --gt-ink.
  const rim: [number, number, number][] = [[5, 0, 4], [3, 1, 8], [2, 2, 10], [1, 3, 12], [1, 4, 12], [0, 5, 14], [0, 6, 14], [0, 7, 14], [0, 8, 14], [1, 9, 12], [1, 10, 12], [2, 11, 10], [3, 12, 8], [5, 13, 4]];
  const ink: [number, number, number][] = [[4, 1, 6], [3, 2, 8], [2, 3, 10], [2, 4, 10], [1, 5, 12], [1, 6, 12], [1, 7, 12], [1, 8, 12], [2, 9, 10], [2, 10, 10], [3, 11, 8], [4, 12, 6]];
  return (
    <svg width={size} height={size} viewBox="0 0 14 14" shapeRendering="crispEdges" style={{ display: 'block' }} aria-hidden="true">
      {rim.map(([x, y, w]) => (
        <rect key={'r' + y} x={x} y={y} width={w} height={1} fill="var(--gt-line)" />
      ))}
      {ink.map(([x, y, w]) => (
        <rect key={'i' + y} x={x} y={y} width={w} height={1} fill="var(--gt-ink)" />
      ))}
    </svg>
  );
}

/**
 * The gold pixel crown over the HOST's avatar in WHO'S IN. "Host" since
 * 2026-08-28: the party's Leader is a different person and a different rank,
 * so game night's lever-holder gave the word back (the component keeps its
 * name — a crown is a crown).
 */
export function LeaderCrown({ style }: { style?: CSSProperties }): JSX.Element {
  return (
    <svg width={16} height={9} viewBox="0 0 8 5" shapeRendering="crispEdges" style={{ display: 'block', ...style }} aria-hidden="true">
      <rect x="0" y="1" width="1" height="4" fill="var(--gt-gold)" />
      <rect x="7" y="1" width="1" height="4" fill="var(--gt-gold)" />
      <rect x="0" y="0" width="1" height="1" fill="var(--gt-gold-lt)" />
      <rect x="7" y="0" width="1" height="1" fill="var(--gt-gold-lt)" />
      <rect x="3" y="0" width="2" height="1" fill="var(--gt-gold-lt)" />
      <rect x="1" y="3" width="6" height="2" fill="var(--gt-gold)" />
      <rect x="2" y="2" width="1" height="1" fill="var(--gt-gold-dk)" />
      <rect x="5" y="2" width="1" height="1" fill="var(--gt-gold-dk)" />
      <rect x="3" y="1" width="2" height="2" fill="var(--gt-gold)" />
      <rect x="1" y="4" width="6" height="1" fill="var(--gt-gold-dk)" />
    </svg>
  );
}

/**
 * THE BORDER WEARS EVERY BACKER'S COLOUR (Marcus, 2026-08-30). One nominator
 * paints the border their hue, exactly as it always has. Two split it down the
 * middle, three into thirds, and so on — left to right, in the order the
 * quarters were spent, with HARD edges rather than a blend, so each share stays
 * a player's colour instead of a smear between two.
 *
 * `border-image` rather than a padded wrapper element: every border this
 * dresses is already one CSS property on a box that exists, and this keeps it
 * that way. The `1` slice is what makes it read as a SPLIT rather than as a
 * gradient — the gradient is drawn at the size of the border box and then
 * sliced a pixel in from each edge, so the top and bottom edges carry the whole
 * left-to-right progression while the left edge is pure first-hue and the right
 * edge pure last. Corners follow the edge they sit on.
 *
 * The one thing `border-image` cannot do is follow a rounded corner, and this
 * design has no rounded corners anywhere — the pixel-art look is square by
 * construction, so the constraint costs nothing.
 *
 * Returns NOTHING below two hues, so the caller's own `border` shorthand is
 * left exactly as it was and the un-seconded case cannot regress.
 */
export function hueSplit(hues: readonly string[]): CSSProperties {
  const n = hues.length;
  if (n < 2) return {};
  const at = (i: number): string => ((i * 100) / n).toFixed(3) + '%';
  const stops = hues.map((h, i) => h + ' ' + at(i) + ' ' + at(i + 1)).join(', ');
  return { borderImage: 'linear-gradient(90deg, ' + stops + ') 1' };
}

/**
 * EVERYONE WITH A QUARTER ON ONE GAME (3054, Marcus 2026-08-30). The same row
 * on the browse tile, the wheel's hover panel and the pin card, so "who backed
 * this" reads identically wherever it appears — which matters here more than
 * usual, because the number of faces IS the reason the slice is that wide.
 *
 * Faces overlap by a third, earliest first, so the stack grows slowly as a
 * crew piles onto one title; anything past the caller's cap becomes `+N`. Each
 * avatar keeps its own `--gt-ink` hairline, which is what separates two hues
 * that happen to be close.
 */
export function NominatorStack({
  bys,
  more = 0,
  size,
  fs,
}: {
  bys: readonly RoundNomByVM[];
  /** how many nominators are NOT drawn — rendered as a trailing `+N` */
  more?: number;
  size: number;
  fs: number;
}): JSX.Element | null {
  if (!bys.length && more <= 0) return null;
  const lap = Math.round(size / 3);
  return (
    <div style={{ display: 'flex', alignItems: 'center', flex: 'none' }}>
      {bys.map((b, i) => (
        <div key={b.key} style={{ display: 'flex', flex: 'none', marginLeft: i ? -lap : 0, zIndex: bys.length - i }}>
          <Avatar size={size} fs={fs} ini={b.ini} bg={b.bg} hasAv={b.hasAv} avBg={b.avBg} title={b.nm} />
        </div>
      ))}
      {more > 0 ? (
        <div
          style={{
            marginLeft: bys.length ? -lap : 0,
            zIndex: 0,
            height: size,
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            // clears the face it overlaps, so the count is never under a hue
            padding: bys.length ? '0 5px 0 ' + (lap + 4) + 'px' : '0 5px',
            border: '1px solid var(--gt-ink)',
            background: 'var(--gt-raised)',
            fontFamily: MONO,
            fontSize: fs + 1,
            color: 'var(--gt-text-4)',
            whiteSpace: 'nowrap',
          }}
        >
          +{more}
        </div>
      ) : null}
    </div>
  );
}

/**
 * An armed destructive control's label pair: resting and `SURE?` share one
 * grid cell so the box never resizes, and the draining `--acc2` bar shows how
 * long the arm has left (changelog 2026-08-26 ×2).
 */
export function ArmedLabel({
  rest,
  restV,
  armV,
  fill,
  anim,
}: {
  rest: string;
  restV: 'visible' | 'hidden';
  armV: 'visible' | 'hidden';
  fill: number;
  anim: boolean;
}): JSX.Element {
  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: '100%',
          background: 'var(--acc2)',
          opacity: fill,
          animation: anim ? 'gt-armdrain 5s linear forwards' : 'none',
          pointerEvents: 'none',
        }}
      />
      <span style={{ position: 'relative', display: 'grid', justifyItems: 'center', alignItems: 'center', fontFamily: MONO }}>
        <span style={{ gridArea: '1/1', whiteSpace: 'nowrap', visibility: restV }}>{rest}</span>
        <span style={{ gridArea: '1/1', whiteSpace: 'nowrap', visibility: armV }}>SURE?</span>
      </span>
    </>
  );
}
